// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — attribution, réceptions et stock d'une campagne :
//   • attribution : calculée par une personne (règle du règlement, résultat déterministe, empreinte, graine publiée
//     pour un tirage), ARRÊTÉE par une autre ; une inscription sans unité est annulée (« stock insuffisant ») ;
//   • réceptions contradictoires : procès-verbal établi avec sa pièce, contresigné par une AUTRE personne (numéro,
//     lot, mouvement de stock), annulé avec motif tant qu'il n'est pas contresigné ;
//   • stock DÉRIVÉ des mouvements (jamais saisi), par lot : reçu, remis, retours, pertes, reventes, en stock.
// Routes : GET/POST /:id/attribution(/calculer), POST /attributions/:id/arreter, POST /commandes/:id/receptions,
// POST /receptions/:id/{contresigner,annuler}, GET /:id/stock, GET /:id/achats.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Boxes, Calculator, ClipboardCheck, FileSignature, History, Lock, MoreVertical, PackageCheck, Undo2 } from 'lucide-react';
import { Bouton, BandeauInfo, MenuActions } from '../../design/composants';
import { toArray, libelle, aujourdhuiDakar } from './api';
import { Champ, Etat, Fenetre } from './commun';
import { dateLongue, dateHeureLongue } from './apiSocial';
import { useLectureSociale, EtatsLecture, FenetreActe, TitreSection, Infos, Tuiles, motifValide, ChampMotif, usePieceDepot, ChampPiece } from './communSocial';
import {
  useCampagnes, quantiteUnites, STATUTS_CALCUL, STATUTS_RECEPTION, REGLES_ATTRIBUTION, ORIGINES_STOCK, NATURES_STOCK,
} from './apiCampagnes';
import { codeEtat, lireEntier } from './communCampagnes';

const ETATS_CALCUL = ['INSCRIPTIONS_CLOSES', 'EN_DISTRIBUTION'];

// ─── Attribution ──────────────────────────────────────────────────────────────────────────────
function Calcul({ campagne, calcul }) {
  const lignes = toArray(calcul.lignes);
  return (
    <>
      <Infos lignes={[
        ['État', <Etat table={STATUTS_CALCUL} code={calcul.statut} />], ['Règle', libelle(REGLES_ATTRIBUTION, calcul.regle)],
        ['Stock de base', `${quantiteUnites(campagne, calcul.stock_base)} — ${libelle(ORIGINES_STOCK, calcul.origine_stock)}`],
        ['Demandé', quantiteUnites(campagne, calcul.demande)], ['Attribué', quantiteUnites(campagne, calcul.attribue)],
        ['Calculé le', dateHeureLongue(calcul.calcule_le)], ['Calculé par', calcul.calcule_par_nom || null],
        ['Arrêté le', calcul.arrete_le ? dateHeureLongue(calcul.arrete_le) : null], ['Arrêté par', calcul.arrete_par_nom || null],
        ['Graine du tirage (publiée)', calcul.graine ? <code style={{ fontSize: 12, wordBreak: 'break-all' }}>{calcul.graine}</code> : null],
        ['Empreinte du résultat', <code title={calcul.empreinte} style={{ fontSize: 12 }}>{String(calcul.empreinte || '').slice(0, 16)}…</code>],
      ]} />
      <div className="rh-conteneur-tableau" style={{ marginTop: 12 }}>
        <table className="rh-tableau">
          <thead><tr><th>Rang</th><th>Salarié</th><th>Demandé</th><th>Attribué</th></tr></thead>
          <tbody>
            {lignes.map((l) => (
              <tr key={l.inscription_id}>
                <td>{l.rang}</td>
                <td>{l.salarie}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{l.numero} · {l.matricule}</span></td>
                <td>{quantiteUnites(campagne, l.demande)}</td>
                <td style={{ fontWeight: 600 }}>{Number(l.quantite) > 0 ? quantiteUnites(campagne, l.quantite) : <span className="rh-cellule-secondaire">Aucune unité (stock insuffisant)</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Attribution({ campagne, droits, onChange }) {
  const api = useCampagnes();
  const l = useLectureSociale(() => api.attributions(campagne.id), [api, campagne.id]);
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  const [anciens, setAnciens] = useState(false);
  const calculs = toArray(l.donnees);
  const courant = [...calculs].reverse().find((x) => ['PROPOSEE', 'ARRETEE'].includes(x.statut)) || calculs[calculs.length - 1];
  const precedents = calculs.filter((x) => x !== courant);
  const peutCalculer = droits.a('ds_attribution_calculer') && ETATS_CALCUL.includes(codeEtat(campagne));
  const fait = (m) => { setFenetre(null); setInfo(m); l.recharger(); if (onChange) onChange(); };
  return (
    <section className="rh-carte">
      <TitreSection action={(
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {peutCalculer && <Bouton petit Icone={Calculator} onClick={() => setFenetre('calculer')}>{courant ? 'Recalculer' : 'Calculer l\'attribution'}</Bouton>}
          {courant?.statut === 'PROPOSEE' && droits.a('ds_attribution_arreter') && <Bouton petit variante="primaire" Icone={Lock} onClick={() => setFenetre('arreter')}>Arrêter l'attribution</Bouton>}
        </div>
      )}>Attribution</TitreSection>
      {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
      {courant?.statut === 'PROPOSEE' && (
        <BandeauInfo ton="alerte" titre="Calcul proposé, à arrêter par une autre personne">La personne qui a calculé l'attribution ne l'arrête pas (double regard).</BandeauInfo>
      )}
      <EtatsLecture lecture={l} Icone={Calculator} estVide={!calculs.length} titreVide="Aucune attribution calculée"
        vide="L'attribution se calcule après la clôture des inscriptions, selon la règle du règlement : le stock de base est le reçu net, à défaut les commandes validées, à défaut la quantité disponible.">
        {courant && <Calcul campagne={campagne} calcul={courant} />}
        {precedents.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Bouton petit variante="lien" onClick={() => setAnciens((x) => !x)}>{anciens ? 'Masquer' : 'Voir'} les calculs précédents ({precedents.length})</Bouton>
            {anciens && precedents.map((x) => <div key={x.id} style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--sg-bord)' }}><Calcul campagne={campagne} calcul={x} /></div>)}
          </div>
        )}
      </EtatsLecture>
      {fenetre === 'calculer' && (
        <FenetreActe titre="Calculer l'attribution" Icone={Calculator} libelleBouton="Calculer" onFermer={() => setFenetre(null)}
          aide="Le résultat est déterministe (le relancer donne la même liste) et porte une empreinte. Un calcul encore proposé est remplacé. Après la première remise, l'attribution ne se recalcule plus."
          onConfirmer={async () => { await api.calculerAttribution(campagne.id); fait('Attribution calculée : elle attend l\'arrêt par une autre personne.'); }}>
          <p style={{ margin: '0 0 12px', fontSize: 14 }}>Règle du règlement : <strong>{libelle(REGLES_ATTRIBUTION, campagne.reglement?.regle_attribution) || 'lue par le serveur'}</strong>.</p>
        </FenetreActe>
      )}
      {fenetre === 'arreter' && courant && (
        <FenetreActe titre="Arrêter l'attribution" Icone={Lock} libelleBouton="Arrêter" onFermer={() => setFenetre(null)}
          aide="Les inscriptions deviennent « attribuées » pour la quantité calculée ; celles sans unité sont annulées (stock insuffisant). Les salariés sont prévenus."
          onConfirmer={async (cle) => { await api.arreterAttribution(courant.id, cle); fait('Attribution arrêtée.'); }}>
          <p style={{ margin: '0 0 12px', fontSize: 14 }}>{quantiteUnites(campagne, courant.attribue)} attribuées pour {quantiteUnites(campagne, courant.demande)} demandées.</p>
        </FenetreActe>
      )}
    </section>
  );
}

// ─── Réceptions ───────────────────────────────────────────────────────────────────────────────
function FenetreReception({ campagne, commandes, onFermer, onFait }) {
  const api = useCampagnes();
  const [commande, setCommande] = useState(commandes.length === 1 ? commandes[0].id : '');
  const [date, setDate] = useState(aujourdhuiDakar());
  const [recue, setRecue] = useState('');
  const [refusee, setRefusee] = useState('');
  const [etat, setEtat] = useState('');
  const piece = usePieceDepot(campagne.organisation_id, 'COMMANDE', commande || null, 'PV_RECEPTION');
  const qr = lireEntier(recue);
  const qf = refusee.trim() ? lireEntier(refusee) : 0;
  const k = commandes.find((x) => x.id === commande);
  const dejaRecu = k ? toArray(k.receptions).filter((r) => r.statut !== 'ANNULEE').reduce((s, r) => s + Number(r.quantite_recue || 0), 0) : 0;
  return (
    <FenetreActe titre="Établir un procès-verbal de réception" Icone={ClipboardCheck} libelleBouton="Établir le procès-verbal" pret={!!(k && date && qr !== null && qf !== null && qr + qf > 0 && piece.prete)} onFermer={onFermer}
      aide="Vous êtes le premier signataire ; une autre personne (commission des achats ou agent de remise) contresigne. Le lot et le mouvement de stock naissent à la contre-signature."
      onConfirmer={async () => {
        const pc = await piece.deposer();
        await api.etablirReception(commande, { date_reception: date, quantite_recue: qr, ...(qf ? { quantite_refusee: qf } : {}), ...(etat.trim() ? { etat_constate: etat.trim() } : {}), piece_pv_id: pc.id });
        onFait('Procès-verbal établi : il attend la contre-signature d\'une autre personne.');
      }}>
      <Champ libelle="Commande" requis aide={k ? `Commandé : ${quantiteUnites(campagne, k.quantite)} · déjà reçu ou à contresigner : ${dejaRecu}` : undefined}>
        <select className="rh-champ-saisie" value={commande} onChange={(e) => setCommande(e.target.value)}>
          <option value="">Choisir…</option>
          {commandes.map((x) => <option key={x.id} value={x.id}>{x.numero} — {x.raison_sociale}</option>)}
        </select>
      </Champ>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
        <Champ libelle="Date de réception" requis><input type="date" className="rh-champ-saisie" value={date} onChange={(e) => setDate(e.target.value)} /></Champ>
        <Champ libelle={`Reçu (${campagne.unite_pluriel})`} requis><input className="rh-champ-saisie" inputMode="numeric" value={recue} onChange={(e) => setRecue(e.target.value)} /></Champ>
        <Champ libelle="Refusé"><input className="rh-champ-saisie" inputMode="numeric" value={refusee} onChange={(e) => setRefusee(e.target.value)} placeholder="0" /></Champ>
      </div>
      <Champ libelle="État constaté (facultatif)"><textarea className="rh-champ-saisie" rows={2} value={etat} onChange={(e) => setEtat(e.target.value)} /></Champ>
      <ChampPiece piece={piece} types={['PV_RECEPTION']} libelleChamp="Procès-verbal de réception (pièce)" />
    </FenetreActe>
  );
}

function Receptions({ campagne, droits }) {
  const api = useCampagnes();
  const achats = useLectureSociale(() => api.achats(campagne.id), [api, campagne.id]);
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  const commandes = toArray(achats.donnees?.commandes);
  const recevables = commandes.filter((k) => ['VALIDEE', 'PARTIELLEMENT_RECUE'].includes(k.statut));
  const pv = commandes.flatMap((k) => toArray(k.receptions).map((r) => ({ ...r, commande: k })));
  const peutContresigner = droits.a('ds_reception_signer') || droits.a('ds_remise_effectuer');
  const fait = (m) => { setFenetre(null); setInfo(m); achats.recharger(); };
  return (
    <section className="rh-carte">
      <TitreSection action={droits.a('ds_reception_signer') && <Bouton petit Icone={ClipboardCheck} disabled={!recevables.length} onClick={() => setFenetre({ type: 'etablir' })}>Établir un procès-verbal</Bouton>}>
        Réceptions
      </TitreSection>
      {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
      <EtatsLecture lecture={achats} Icone={PackageCheck} estVide={!pv.length} titreVide="Aucune réception"
        vide={recevables.length ? 'Établissez le procès-verbal à la livraison : il est contresigné par une autre personne.' : 'Les réceptions s\'établissent sur une commande validée.'}>
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau">
            <thead><tr><th>Procès-verbal</th><th>Commande</th><th>Reçu</th><th>Refusé</th><th>État</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {pv.map((r) => {
                const items = r.statut === 'A_CONTRESIGNER' ? [
                  peutContresigner && { libelle: 'Contresigner', Icone: FileSignature, onClick: () => setFenetre({ type: 'contresigner', r }) },
                  droits.a('ds_reception_signer') && { libelle: 'Annuler le procès-verbal', Icone: Undo2, danger: true, onClick: () => setFenetre({ type: 'annuler', r }) },
                ].filter(Boolean) : [];
                return (
                  <tr key={r.id}>
                    <td>{r.numero || 'Numéro à la contre-signature'}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>reçu le {dateLongue(r.date_reception)}</span>
                      {(r.signataire_1_nom || r.signataire_2_nom) && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{r.signataire_1_nom ? `signé par ${r.signataire_1_nom}` : ''}{r.signataire_2_nom ? `${r.signataire_1_nom ? ', ' : ''}contresigné par ${r.signataire_2_nom}` : ''}</span>}</td>
                    <td>{r.commande.numero}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{r.commande.raison_sociale}</span></td>
                    <td>{quantiteUnites(campagne, r.quantite_recue)}</td>
                    <td>{Number(r.quantite_refusee) > 0 ? quantiteUnites(campagne, r.quantite_refusee) : '—'}</td>
                    <td><Etat table={STATUTS_RECEPTION} code={r.statut} /></td>
                    <td style={{ textAlign: 'right' }}>{items.length > 0 && <MenuActions iconeSeule gauche Icone={MoreVertical} libelle="Actions sur le procès-verbal" items={items} />}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </EtatsLecture>
      {fenetre?.type === 'etablir' && <FenetreReception campagne={campagne} commandes={recevables} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'contresigner' && (
        <FenetreActe titre="Contresigner le procès-verbal" Icone={FileSignature} libelleBouton="Contresigner" onFermer={() => setFenetre(null)}
          aide="Le premier signataire ne contresigne pas. La contre-signature attribue le numéro, crée le lot et le mouvement de stock ; le procès-verbal est ensuite figé (un écart se déclare)."
          onConfirmer={async () => { await api.contresignerReception(fenetre.r.id); fait('Procès-verbal contresigné : le lot est en stock.'); }}>
          <p style={{ margin: '0 0 12px', fontSize: 14 }}>{quantiteUnites(campagne, fenetre.r.quantite_recue)} reçues le {dateLongue(fenetre.r.date_reception)} — commande {fenetre.r.commande.numero}.</p>
        </FenetreActe>
      )}
      {fenetre?.type === 'annuler' && <FenetreAnnulerPv reception={fenetre.r} onFermer={() => setFenetre(null)} onFait={fait} />}
    </section>
  );
}

function FenetreAnnulerPv({ reception, onFermer, onFait }) {
  const api = useCampagnes();
  const [motif, setMotif] = useState('');
  return (
    <FenetreActe titre="Annuler le procès-verbal" Icone={Undo2} libelleBouton="Annuler le procès-verbal" danger pret={motifValide(motif, 5)} onFermer={onFermer}
      aide="Possible tant qu'il n'est pas contresigné."
      onConfirmer={async () => { await api.annulerReception(reception.id, motif.trim()); onFait('Procès-verbal annulé.'); }}>
      <ChampMotif valeur={motif} onChange={setMotif} />
    </FenetreActe>
  );
}

// ─── Stock ────────────────────────────────────────────────────────────────────────────────────
const NATURES_MOUVEMENT_STOCK = {
  RECEPTION: 'Réception', REMISE: 'Remise à un salarié', RETOUR_SALARIE: 'Retour d\'un salarié', PERTE: 'Perte', VOL: 'Vol', REVENTE: 'Revente',
  RETOUR_FOURNISSEUR: 'Retour au fournisseur', CORRECTION: 'Correction',
};

/** Mouvements détaillés d'un lot (route lue si le serveur la propose ; sinon « bientôt disponible »). */
function FenetreMouvementsStock({ campagne, lot, onFermer }) {
  const api = useCampagnes();
  const l = useLectureSociale(() => api.mouvementsStock(campagne.id, lot.lot_id), [api, campagne.id, lot.lot_id]);
  const mvts = toArray(l.donnees?.mouvements || l.donnees);
  return (
    <Fenetre titre={`Mouvements — ${lot.libelle}`} Icone={History} onFermer={onFermer} large>
      <EtatsLecture lecture={l} Icone={History} estVide={!mvts.length} titreVide="Aucun mouvement" vide="Aucun mouvement enregistré pour ce lot.">
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau">
            <thead><tr><th>Date</th><th>Mouvement</th><th style={{ textAlign: 'right' }}>Quantité</th><th>Pièce</th><th>Par</th></tr></thead>
            <tbody>
              {mvts.map((m, i) => {
                const q = Number(m.quantite || 0);
                const sortie = m.sens !== undefined ? Number(m.sens) < 0 : q < 0;
                return (
                  <tr key={m.id || `${m.le || m.date}-${i}`}>
                    <td style={{ whiteSpace: 'nowrap' }}>{m.le ? dateHeureLongue(m.le) : dateLongue(m.date)}</td>
                    <td>{NATURES_MOUVEMENT_STOCK[m.nature] || libelle(NATURES_STOCK, m.nature) || m.nature}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{sortie ? '− ' : '+ '}{quantiteUnites(campagne, Math.abs(q))}</td>
                    <td>{m.piece || m.piece_numero || m.reference || m.source_numero || '—'}</td>
                    <td>{m.acteur || m.par || m.acteur_nom || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </EtatsLecture>
    </Fenetre>
  );
}

function Stock({ campagne }) {
  const api = useCampagnes();
  const s = useLectureSociale(() => api.stock(campagne.id), [api, campagne.id]);
  const [lot, setLot] = useState(null);
  const lots = toArray(s.donnees?.lots);
  const colonnes = Object.keys(NATURES_STOCK);
  return (
    <section className="rh-carte">
      <TitreSection>Lots et stock</TitreSection>
      <p className="rh-form-aide" style={{ margin: '0 0 8px' }}>Stock calculé à partir des mouvements (réception, remise, retour, perte, revente), jamais saisi. Pertes, retours et invendus se déclarent comme des écarts.</p>
      <EtatsLecture lecture={s} Icone={Boxes} estVide={!lots.length} titreVide="Aucun lot" vide="Un lot naît de chaque procès-verbal de réception contresigné.">
        <Tuiles tuiles={colonnes.map((k) => ({ libelle: NATURES_STOCK[k], valeur: String(s.donnees?.[k] ?? 0) }))} />
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau">
            <thead><tr><th>Lot</th>{colonnes.map((k) => <th key={k} style={{ textAlign: 'right' }}>{NATURES_STOCK[k]}</th>)}<th aria-label="Mouvements" /></tr></thead>
            <tbody>
              {lots.map((l) => (
                <tr key={l.lot_id}>
                  <td>{l.libelle}</td>
                  {colonnes.map((k) => <td key={k} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: k === 'en_stock' ? 600 : 400 }}>{l[k]}</td>)}
                  <td style={{ textAlign: 'right' }}><Bouton petit variante="discret" Icone={History} onClick={() => setLot(l)}>Mouvements</Bouton></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </EtatsLecture>
      {lot && <FenetreMouvementsStock campagne={campagne} lot={lot} onFermer={() => setLot(null)} />}
    </section>
  );
}

export default function DistributionVue({ campagne, droits, onChange }) {
  return (
    <>
      <Attribution campagne={campagne} droits={droits} onChange={onChange} />
      <Receptions campagne={campagne} droits={droits} />
      <Stock campagne={campagne} />
    </>
  );
}
